import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Plus,
  Edit,
  Trash2,
  Users,
  CheckCircle,
  XCircle,
  BookOpen,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { classroomsApi, sectionsApi, optionsApi, Classroom, Section, Option } from '../../services/schoolApi';
import ConfirmDialog from '../../components/ConfirmDialog';

const Classes = () => {
  const { language } = useLanguage();
  
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [classroomToDelete, setClassroomToDelete] = useState<string | null>(null);
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClassroom, setEditingClassroom] = useState<Classroom | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterLevel, setFilterLevel] = useState('');
  const [filterYear, setFilterYear] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    level: '',
    capacity: '',
    schoolYear: '',
    sectionId: '',
    optionId: '',
    description: '',
  });

  const text = {
    fr: {
      title: 'Gestion des Classes',
      description: 'Liste et gestion de toutes les classes',
      addClass: 'Ajouter une classe',
      name: 'Nom de la classe',
      level: 'Niveau',
      capacity: 'Capacité',
      schoolYear: 'Année académique',
      section: 'Section',
      option: 'Option',
      description: 'Description',
      students: 'Élèves',
      actions: 'Actions',
      edit: 'Modifier',
      delete: 'Supprimer',
      active: 'Active',
      inactive: 'Inactive',
      cancel: 'Annuler',
      save: 'Enregistrer',
      editClass: 'Modifier la classe',
      deleteConfirm: 'Êtes-vous sûr de vouloir supprimer cette classe ?',
      noClasses: 'Aucune classe trouvée',
      loading: 'Chargement...',
      filterLevel: 'Filtrer par niveau',
      filterYear: 'Filtrer par année',
      all: 'Tous',
      primary: 'Primaire',
      secondary: 'Secondaire',
      status: 'Statut',
      toggleStatus: 'Activer/Désactiver',
      stats: 'Statistiques',
      totalClasses: 'Total Classes',
      activeClasses: 'Classes Actives',
      totalStudents: 'Total Élèves',
    },
    en: {
      title: 'Classes Management',
      description: 'List and manage all classes',
      addClass: 'Add Class',
      name: 'Class Name',
      level: 'Level',
      capacity: 'Capacity',
      schoolYear: 'Academic Year',
      section: 'Section',
      option: 'Option',
      description: 'Description',
      students: 'Students',
      actions: 'Actions',
      edit: 'Edit',
      delete: 'Delete',
      active: 'Active',
      inactive: 'Inactive',
      cancel: 'Cancel',
      save: 'Save',
      editClass: 'Edit Class',
      deleteConfirm: 'Are you sure you want to delete this class?',
      noClasses: 'No classes found',
      loading: 'Loading...',
      filterLevel: 'Filter by level',
      filterYear: 'Filter by year',
      all: 'All',
      primary: 'Primary',
      secondary: 'Secondary',
      status: 'Status',
      toggleStatus: 'Toggle Status',
      stats: 'Statistics',
      totalClasses: 'Total Classes',
      activeClasses: 'Active Classes',
      totalStudents: 'Total Students',
    }
  };

  const currentText = text[language as keyof typeof text];

  const levels = ['Primaire', 'Secondaire', 'Maternelle'];
  const academicYears = ['2024-2025', '2025-2026', '2026-2027'];

  useEffect(() => {
    fetchClassrooms();
    fetchSections();
  }, [page, filterLevel, filterYear]);

  const fetchClassrooms = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching classrooms...');
      const response = await classroomsApi.getAll({
        page,
        limit: 10,
        level: filterLevel || undefined,
        schoolYear: filterYear || undefined,
      });
      console.log('Classrooms response:', response);
      setClassrooms(response.data);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      console.error('Error fetching classrooms:', err);
      setError(err.message || 'Erreur lors du chargement des classes');
      setClassrooms([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSections = async () => {
    try {
      const sectionsData = await sectionsApi.getAll();
      setSections(Array.isArray(sectionsData) ? sectionsData : []);
    } catch (err) {
      console.error('Erreur lors du chargement des sections:', err);
      setSections([]);
    }
  };

  const fetchOptions = async (sectionId: string) => {
    try {
      const optionsData = await optionsApi.getAll(sectionId);
      setOptions(Array.isArray(optionsData) ? optionsData : []);
    } catch (err) {
      console.error('Erreur lors du chargement des options:', err);
      setOptions([]);
    }
  };

  const handleOpenDialog = (classroom?: Classroom) => {
    if (classroom) {
      setEditingClassroom(classroom);
      setFormData({
        name: classroom.name,
        level: classroom.level,
        capacity: classroom.capacity?.toString() || '',
        schoolYear: classroom.schoolYear,
        sectionId: classroom.sectionId ? (typeof classroom.sectionId === 'string' ? classroom.sectionId : classroom.sectionId._id) : '',
        optionId: classroom.optionId ? (typeof classroom.optionId === 'string' ? classroom.optionId : classroom.optionId._id) : '',
        description: classroom.description || '',
      });
      if (classroom.sectionId) {
        const sectionId = typeof classroom.sectionId === 'string' ? classroom.sectionId : classroom.sectionId._id;
        fetchOptions(sectionId);
      }
    } else {
      setEditingClassroom(null);
      setFormData({
        name: '',
        level: '',
        capacity: '',
        schoolYear: academicYears[1],
        sectionId: '',
        optionId: '',
        description: '',
      });
      setOptions([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClassroom(null);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        capacity: formData.capacity ? parseInt(formData.capacity) : undefined,
      };
      
      if (editingClassroom) {
        await classroomsApi.update(editingClassroom._id, data);
      } else {
        await classroomsApi.create(data);
      }
      handleCloseDialog();
      fetchClassrooms();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteClick = (id: string) => {
    setClassroomToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (classroomToDelete) {
      try {
        await classroomsApi.delete(classroomToDelete);
        setDeleteDialogOpen(false);
        setClassroomToDelete(null);
        fetchClassrooms();
      } catch (err: any) {
        setError(err.message || 'Erreur lors de la suppression');
        setDeleteDialogOpen(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setClassroomToDelete(null);
  };

  const handleToggleActive = async (id: string) => {
    try {
      await classroomsApi.toggleActive(id);
      fetchClassrooms();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la modification du statut');
    }
  };

  const handleSectionChange = (sectionId: string) => {
    setFormData({ ...formData, sectionId, optionId: '' });
    if (sectionId) {
      fetchOptions(sectionId);
    } else {
      setOptions([]);
    }
  };

  const getSectionName = (sectionId?: Section | string) => {
    if (!sectionId) return '-';
    if (typeof sectionId === 'object') return sectionId.name;
    const section = sections.find(s => s._id === sectionId);
    return section?.name || '-';
  };

  const getOptionName = (optionId?: Option | string) => {
    if (!optionId) return '-';
    if (typeof optionId === 'object') return optionId.name;
    const option = options.find(o => o._id === optionId);
    return option?.name || '-';
  };

  const stats = {
    total: classrooms.length,
    active: classrooms.filter(c => c.active).length,
    students: classrooms.reduce((sum, c) => sum + (c.studentCount || 0), 0),
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 1 }}>
            {currentText.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentText.description}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          onClick={() => handleOpenDialog()}
          sx={{ 
            backgroundColor: '#0D3C4C',
            '&:hover': { backgroundColor: '#1A5F7A' }
          }}
        >
          {currentText.addClass}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BookOpen size={40} color="#0D3C4C" />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentText.totalClasses}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle size={40} color="#4caf50" />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {stats.active}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentText.activeClasses}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Users size={40} color="#2196f3" />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {stats.students}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentText.totalStudents}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{currentText.filterLevel}</InputLabel>
            <Select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              label={currentText.filterLevel}
            >
              <MenuItem value="">{currentText.all}</MenuItem>
              {levels.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{currentText.filterYear}</InputLabel>
            <Select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              label={currentText.filterYear}
            >
              <MenuItem value="">{currentText.all}</MenuItem>
              {academicYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.name}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.level}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.schoolYear}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.section}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.option}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.capacity}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.students}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.status}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>{currentText.loading}</Typography>
                </TableCell>
              </TableRow>
            ) : classrooms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{currentText.noClasses}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              classrooms.map((classroom) => (
                <TableRow key={classroom._id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{classroom.name}</TableCell>
                  <TableCell>{classroom.level}</TableCell>
                  <TableCell>{classroom.schoolYear}</TableCell>
                  <TableCell>{getSectionName(classroom.sectionId)}</TableCell>
                  <TableCell>{getOptionName(classroom.optionId)}</TableCell>
                  <TableCell>{classroom.capacity || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      icon={<Users size={16} />}
                      label={classroom.studentCount || 0}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={classroom.active ? <CheckCircle size={16} /> : <XCircle size={16} />}
                      label={classroom.active ? currentText.active : currentText.inactive}
                      color={classroom.active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(classroom._id)}
                      sx={{ color: classroom.active ? '#d32f2f' : '#4caf50' }}
                      title={currentText.toggleStatus}
                    >
                      {classroom.active ? <XCircle size={18} /> : <CheckCircle size={18} />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(classroom)}
                      sx={{ color: '#0D3C4C' }}
                    >
                      <Edit size={18} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(classroom._id)}
                      sx={{ color: '#d32f2f' }}
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingClassroom ? currentText.editClass : currentText.addClass}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
            <TextField
              label={currentText.name}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>{currentText.level}</InputLabel>
              <Select
                value={formData.level}
                onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                label={currentText.level}
              >
                {levels.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={currentText.capacity}
              type="number"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>{currentText.schoolYear}</InputLabel>
              <Select
                value={formData.schoolYear}
                onChange={(e) => setFormData({ ...formData, schoolYear: e.target.value })}
                label={currentText.schoolYear}
              >
                {academicYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{currentText.section}</InputLabel>
              <Select
                value={formData.sectionId}
                onChange={(e) => handleSectionChange(e.target.value)}
                label={currentText.section}
              >
                <MenuItem value="">-</MenuItem>
                {sections.map((section) => (
                  <MenuItem key={section._id} value={section._id}>
                    {section.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{currentText.option}</InputLabel>
              <Select
                value={formData.optionId}
                onChange={(e) => setFormData({ ...formData, optionId: e.target.value })}
                label={currentText.option}
                disabled={!formData.sectionId}
              >
                <MenuItem value="">-</MenuItem>
                {options.map((option) => (
                  <MenuItem key={option._id} value={option._id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={currentText.description}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              sx={{ gridColumn: '1 / -1' }}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{currentText.cancel}</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{ 
              backgroundColor: '#0D3C4C',
              '&:hover': { backgroundColor: '#1A5F7A' }
            }}
          >
            {currentText.save}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title={language === 'fr' ? 'Supprimer la classe' : 'Delete Class'}
        message={language === 'fr' 
          ? 'Êtes-vous sûr de vouloir supprimer cette classe ? Cette action est irréversible et supprimera toutes les données associées.'
          : 'Are you sure you want to delete this class? This action is irreversible and will delete all associated data.'}
        confirmText={language === 'fr' ? 'Supprimer' : 'Delete'}
        cancelText={currentText.cancel}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
      />
    </Container>
  );
};

export default Classes;
